import {
  ElementPropertyType,
  IElementExpressionProperty,
  IElementProperty,
  IElementSelectProperty,
  IElementSwitchProperty,
  IFactoryExpressionProperty,
  IFactorySelectProperty,
  IFactorySwitchProperty,
} from './element-property.model';
import { OneOfElements } from './project.model';
import {
  NotArray,
  OptionalProperties,
  RequireProperties,
} from './type-helpers.model';
import { QuantityUnit } from './unit.model';

export const areaQuantityNames = [
  /**
   * Length x Height
   */
  'area_side',

  /**
   * Length x Width
   */
  'area_top',

  /**
   * Width x Height
   */
  'area_section',
] as const;
export type AreaQuantityName = (typeof areaQuantityNames)[number];

/**
 * Area units used to calculate area of an element.
 * A wall has an area in area_side, a floor has an area in area_top.
 * area_section is never used for this
 */
export type AreaUnit = Extract<
  ElementQuantityExpressionName,
  'area_side' | 'area_top'
>;

export const REPEATING_DIRECTION_NAME = 'repeating_direction';

export const dimensionQuantityNames = ['length', 'width', 'height'] as const;
export type DimensionQuantityName = (typeof dimensionQuantityNames)[number];

/**
 * Thickness units used to calculate thickness of an element.
 * A wall has a thickness in width, a floor has a thickness in height.
 */
export type ThicknessUnit = Extract<
  ElementQuantityExpressionName,
  'height' | 'width'
>;

export const elementQuantitySwitchNames = ['repeating_items'] as const;
export const elementQuantitySelectNames = [REPEATING_DIRECTION_NAME] as const;
export const elementQuantityRepeatingItemExpressionNames = [
  'repeating_item_spacing',
  'repeating_item_thickness',
  'repeating_item_count',
] as const;

/**
 * Names of all repeating item properties that should show when toggling repeating items on
 */
export const elementQuantityRepeatingChildNames = [
  ...elementQuantityRepeatingItemExpressionNames,
  REPEATING_DIRECTION_NAME,
] as const;

export type RepeatingItemQuantityName =
  (typeof elementQuantityRepeatingChildNames)[number];

const topElementQuantityExpressionNames = [
  ...dimensionQuantityNames,
  ...areaQuantityNames,
  'volume',
] as const;

const bottomElementQuantityExpressionNames = [
  'fill',
  'density',
  'density_areal_side',
  'density_areal_top',
  'mass',
  'pieces',
  'time',
  'energy',
] as const;

/**
 * Names of quantities that should be shown as icons in the UI. In order of appearance.
 */
export const iconQuantityNames = [
  'length',
  'area_section',
  'width',
  'area_side',
  'height',
  'area_top',
  'volume',
  'mass',
  'fill',
] as const;

export type IconQuantityName = (typeof iconQuantityNames)[number];

/**
 * Available quantities names. In order of appearance in the UI.
 */
export const elementQuantityExpressionNames = [
  ...topElementQuantityExpressionNames,
  ...elementQuantityRepeatingItemExpressionNames,
  ...bottomElementQuantityExpressionNames,
  // Not yet supported
  // 'filling',
  // 'distance',
] as const;

export const elementQuantityNames = [
  ...elementQuantityExpressionNames,
  ...elementQuantitySwitchNames,
  ...elementQuantitySelectNames,
] as const;

export const sortedElementQuantityNames = [
  ...topElementQuantityExpressionNames,
  ...elementQuantitySwitchNames,
  ...elementQuantitySelectNames,
  ...elementQuantityRepeatingItemExpressionNames,
  ...bottomElementQuantityExpressionNames,
] as const;

export type ElementQuantityExpressionName =
  (typeof elementQuantityExpressionNames)[number];

export type ElementQuantitySwitchName =
  (typeof elementQuantitySwitchNames)[number];

export type ElementQuantitySelectName =
  (typeof elementQuantitySelectNames)[number];

export type ElementQuantityName = (typeof elementQuantityNames)[number];

/**
 * Quantity name for a user-entered piece count.
 * Hidden from the right panel; selectable as selectedQuantity on an element.
 */
export const PIECES_QUANTITY_NAME =
  'pieces' satisfies ElementQuantityExpressionName;

/**
 * Factory defaults for {@link PIECES_QUANTITY_NAME}: hidden, inherited, never auto-quantified.
 */
export const PIECES_QUANTITY_FACTORY = {
  hidden: true,
  inheritFallback: true,
} as const;

/**
 * Quantities the element-list unit menu can switch between.
 * Broader than {@link iconQuantityNames} so hidden rows like pieces stay selectable.
 */
export const elementListSelectableQuantityNames = [
  ...iconQuantityNames,
  PIECES_QUANTITY_NAME,
] as const;

// export const elementQuantities = Object.values(ElementQuantity) as const

export const elementQuantityDisplayNameRecord: Partial<
  Record<ElementQuantityName, string>
> = {
  length: 'Length',
  width: 'Width',
  height: 'Height',
  area_side: 'Side area',
  area_top: 'Top area',
  area_section: 'Section area',
  volume: 'Volume',
  mass: 'Mass',
  pieces: 'Pieces',
  fill: 'Fill',
  density: 'Density',
  density_areal_side: 'Areal density (side)',
  density_areal_top: 'Areal density (top)',
  time: 'Time',
  energy: 'Energy',
  repeating_items: 'Repeating items',
  repeating_direction: 'Repeating direction',
  repeating_item_spacing: 'Item spacing',
  repeating_item_thickness: 'Item thickness',
  repeating_item_count: 'Item count',
};

export const elementQuantityUnitRecord: Record<
  ElementQuantityExpressionName,
  QuantityUnit
> = {
  repeating_item_spacing: 'm',
  repeating_item_thickness: 'm',
  repeating_item_count: 'pcs',
  width: 'm',
  height: 'm',
  length: 'm',
  area_side: 'm²',
  area_top: 'm²',
  area_section: 'm²',
  volume: 'm³',
  density: 'kg/m³',
  density_areal_side: 'kg/m²',
  density_areal_top: 'kg/m²',
  mass: 'kg',
  pieces: 'pcs',
  fill: '%',
  time: 'hr',
  energy: 'kWh',
};

interface IQuantityPropertyBase {
  readonly?: boolean;
  displayName?: string;
}

export interface IElementQuantityExpressionProperty
  extends
    RequireProperties<IElementExpressionProperty, 'fallbackCount'>,
    IQuantityPropertyBase {
  name: keyof ElementQuantityExpressionRecord;
}

export interface IElementQuantitySwitchProperty
  extends IElementSwitchProperty, IQuantityPropertyBase {
  name: ElementQuantitySwitchName;
}

export interface IElementQuantitySelectProperty
  extends IElementSelectProperty, IQuantityPropertyBase {
  name: ElementQuantitySelectName;
}

export type IElementQuantityProperty =
  | IElementQuantityExpressionProperty
  | IElementQuantitySwitchProperty
  | IElementQuantitySelectProperty;

export type ElementQuantityExpressionRecord = Partial<
  Record<ElementQuantityExpressionName, IElementQuantityExpressionProperty>
>;

export type ElementQuantitySwitchRecord = Partial<
  Record<ElementQuantitySwitchName, IElementQuantitySwitchProperty>
>;

export type ElementQuantitySelectRecord = Partial<
  Record<ElementQuantitySelectName, IElementQuantitySelectProperty>
>;

export type ElementQuantityRecord = ElementQuantityExpressionRecord &
  ElementQuantitySwitchRecord &
  ElementQuantitySelectRecord;

export type ElementQuantityTypeByName<T extends ElementQuantityName> =
  Required<ElementQuantityRecord>[T];

export interface IFactoryQuantityExpressionProperty extends OptionalProperties<
  IFactoryExpressionProperty,
  'type'
> {
  name: ElementQuantityExpressionName;
}

export interface IFactoryQuantitySwitchProperty extends OptionalProperties<
  IFactorySwitchProperty,
  'type'
> {
  name: ElementQuantitySwitchName;
}

export interface IFactoryQuantitySelectProperty extends OptionalProperties<
  IFactorySelectProperty,
  'type'
> {
  name: ElementQuantitySelectName;
}

export type IFactoryQuantityProperty =
  | IFactoryQuantityExpressionProperty
  | IFactoryQuantitySwitchProperty
  | IFactoryQuantitySelectProperty;

export type ElementOrQuantityRecord = OneOfElements | ElementQuantityRecord;

export type ElementPropertyOrQuantityProperty =
  IElementProperty | IElementQuantityProperty | undefined;

/**
 * Map of property types to their corresponding quantity interfaces
 */
export type ElementQuantityPropertyTypeMap = {
  [ElementPropertyType.Expression]: IElementQuantityExpressionProperty;
  [ElementPropertyType.Select]: IElementQuantitySelectProperty;
  [ElementPropertyType.Switch]: IElementQuantitySwitchProperty;
};

/**
 * Use FactoryPropertyInputTypeMap to get correct interface based on property type
 */
// export type FactoryPropertyInputTypeMap<Property extends ElementQuantityName> = {
//   [Property in ElementQuantityExpressionName]: IFactoryExpressionProperty;
//   [Property in ElementQuantitySelectName]: IFactoryQuantitySelectProperty;
//   [Property in ElementQuantitySwitchName]: IFactorySwitchProperty;
// };

export type FactoryQuantityRecord = NotArray<
  Partial<
    Record<
      ElementQuantityExpressionName,
      | OptionalProperties<IFactoryQuantityExpressionProperty, 'name'>
      | IFactoryQuantityExpressionProperty['count']
    > &
      Record<
        ElementQuantitySwitchName,
        | OptionalProperties<IFactoryQuantitySwitchProperty, 'name'>
        | IFactoryQuantitySwitchProperty['count']
      > &
      Record<
        ElementQuantitySelectName,
        | OptionalProperties<IFactoryQuantitySelectProperty, 'name'>
        | IFactoryQuantitySelectProperty['count']
      >
  >
>;

export type FactoryQuantityTypeByName<T extends ElementQuantityName> = (Record<
  ElementQuantityExpressionName,
  IFactoryQuantityExpressionProperty
> &
  Record<ElementQuantitySwitchName, IFactoryQuantitySwitchProperty> &
  Record<ElementQuantitySelectName, IFactoryQuantitySelectProperty>)[T];

export const DEFAULT_QUANTITY_EXPRESSION = '0';
export const DEFAULT_QUANTITY_EXPRESSIONS: Record<
  ElementQuantityExpressionName,
  string
> = {
  /**
   * This will return density of the first child product
   */
  density: 'densityArray[1]',
  density_areal_side: 'densityPerSqmArray[1]',
  density_areal_top: 'densityPerSqmArray[1]',
  fill: '100%',
  repeating_item_spacing: DEFAULT_QUANTITY_EXPRESSION,
  repeating_item_thickness: DEFAULT_QUANTITY_EXPRESSION,
  repeating_item_count: DEFAULT_QUANTITY_EXPRESSION,
  mass: DEFAULT_QUANTITY_EXPRESSION,
  pieces: DEFAULT_QUANTITY_EXPRESSION,
  area_side: DEFAULT_QUANTITY_EXPRESSION,
  area_top: DEFAULT_QUANTITY_EXPRESSION,
  area_section: DEFAULT_QUANTITY_EXPRESSION,
  length: DEFAULT_QUANTITY_EXPRESSION,
  width: DEFAULT_QUANTITY_EXPRESSION,
  height: DEFAULT_QUANTITY_EXPRESSION,
  volume: DEFAULT_QUANTITY_EXPRESSION,
  time: DEFAULT_QUANTITY_EXPRESSION,
  energy: DEFAULT_QUANTITY_EXPRESSION,
};

/**
 * If nothing is special with EC quantities this can be used to generate them
 * @returns
 */
export const DEFAULT_QUANTITY_PROPERTIES: Readonly<FactoryQuantityRecord> = {
  mass: {},
  pieces: { ...PIECES_QUANTITY_FACTORY },
  area_side: {},
  area_top: {},
  area_section: {},
  length: {},
  width: {},
  height: {},
  density: {
    fallbackCount: DEFAULT_QUANTITY_EXPRESSIONS.density,
  },
  volume: {},
  fill: { min: 0, fallbackCount: DEFAULT_QUANTITY_EXPRESSIONS.fill },
  repeating_items: {},
  repeating_direction: {
    type: ElementPropertyType.Select,
    count: 'horizontal',
    options: [
      { label: 'Horizontal', value: 'horizontal' },
      { label: 'Vertical', value: 'vertical' },
    ],
  },
  repeating_item_spacing: {},
  repeating_item_thickness: {},
  repeating_item_count: {},
};

/**
 * Product category elements that have areal density properties
 */
export const DEFAULT_AREAL_DENSITY_PROPERTIES: Readonly<FactoryQuantityRecord> =
  {
    density_areal_side: {
      hidden: true,
      fallbackCount: DEFAULT_QUANTITY_EXPRESSIONS.density_areal_side,
    },
    density_areal_top: {
      hidden: true,
      fallbackCount: DEFAULT_QUANTITY_EXPRESSIONS.density_areal_top,
    },
  };
